import axios from 'axios'
import { jwtDecode } from 'jwt-decode'

import {
  DecodedAccessToken,
  DecodedRefreshToken,
  getOrGenerateDeviceID,
  getStoredLogin,
  getStoredRefreshToken,
  isRunningInWebview,
  removeStoredRefreshToken,
  storeLogin,
} from './graphql-client/storedState'
import { getStoredLocale } from './util/locale'

interface RenewResponse {
  data: {
    accessToken: string
    refreshToken: string
    locationToken: string
  }
}

let isRefreshing = false
let refreshPromise: Promise<RenewResponse | null> | null = null


export const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
  'application-id': isRunningInWebview() ? 'innomobiili' : 'innohinaus-app',
  'x-device-id': getOrGenerateDeviceID(),
  'accept-language': getStoredLocale()
})

// Base instance shared by all REST calls — sets common headers but no Authorization.
// Use this for pre-auth requests (token renewal, logout) where Authorization is set per-request.
export const baseAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_REST_API_URL,
  headers: getDefaultHeaders(),
})

baseAxiosInstance.interceptors.request.use(config => {
  config.headers['accept-language'] = getStoredLocale()
  return config
})

// GraphQL doesn't support multipart/form-data, so use axios for file uploads
export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_REST_API_URL,
  headers: getDefaultHeaders(),
})

axiosInstance.interceptors.request.use(config => {
  config.headers['accept-language'] = getStoredLocale()
  return config
})

type UserDataListener = (accessToken: string) => void
const userDataListeners: UserDataListener[] = []

export const onUserDataChanged = (listener: UserDataListener) => {
  userDataListeners.push(listener)
  return () => {
    const index = userDataListeners.indexOf(listener)
    if (index > -1) {
      userDataListeners.splice(index, 1)
    }
  }
}

export const notifyUserDataChanged = (accessToken: string) => {
  userDataListeners.forEach(listener => listener(accessToken))
}

export function isExpired(exp: number): boolean {
  return Number(exp) * 1000 < Date.now()
}

export function isAccessTokenExpired(): boolean {
  const accessToken = getStoredLogin()
  if (!accessToken) {
    return true
  }
  const decoded = jwtDecode<DecodedAccessToken>(accessToken)
  return isExpired(decoded.exp)
}

export function isRefreshTokenExpired(): boolean {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) {
    return true
  }
  const decoded = jwtDecode<DecodedRefreshToken>(refreshToken)
  return isExpired(decoded.exp)
}

export const renewTokens = async (): Promise<RenewResponse | null> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const refreshToken = getStoredRefreshToken()
      if (!refreshToken) {
        return null
      }

      const decoded = jwtDecode<DecodedRefreshToken>(refreshToken)
      if (isExpired(decoded.exp)) {
        return null
      }

      const { data: response } = await baseAxiosInstance.post<RenewResponse>('/auth/token/renew', {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`
        },
      })

      const { accessToken: newAccessToken, refreshToken: newRefreshToken, locationToken: newLocationToken } = response.data
      storeLogin({ accessToken: newAccessToken, refreshToken: newRefreshToken, locationToken: newLocationToken })
      notifyUserDataChanged(newAccessToken)

      return response
    } catch (error) {

      const message = error instanceof Error ? error.message : String(error)
      console.error(`Token renewal failed: ${message}`)
      removeStoredRefreshToken()
      return null
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export const getValidAccessToken = async (): Promise<string | null> => {
  let accessToken = getStoredLogin()
  const refreshToken = getStoredRefreshToken()

  if (!accessToken && !refreshToken) {
    return null
  }

  if (accessToken) {
    const decoded = jwtDecode<DecodedAccessToken>(accessToken)
    if (isExpired(decoded.exp)) {
      accessToken = null
    }
  }

  if (!accessToken && refreshToken) {
    const result = await renewTokens()
    if (result) {
      accessToken = result.data.accessToken
    }
  }

  return accessToken
}

axiosInstance.interceptors.request.use(
  async request => {
    const token = await getValidAccessToken()
    if (token) {
      request.headers['Authorization'] = `Bearer ${token}`
    }
    return request
  },
  error => Promise.reject(error)
)
